/* eslint-disable no-underscore-dangle,graphql/template-strings */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
// adopt
import { adopt } from 'react-adopt';
// graphQL
import gql from 'graphql-tag';
// global variables
import {
  defaultNumberOfRows as DEFAULT_NUMBER_OF_ROWS,
  processorRulesStatus,
} from 'config';
// our components
import qs, { getQueryString } from 'helpers/queryStrings';
import RouterContext from 'helpers/routerContext';
import { bulkUpdate } from 'helpers/updateMutationsHelper';
import getProcessorTargetVariables from 'helpers/processorTargetVariables';
import { Query, Mutation } from 'components/common/Query';
import { PageInfo } from 'components/common/Paginator';
import ProcessorRuleList from './List';

/**
 * Query to fetch processor rules from api
 */
export const PROCESSOR_RULES_QUERY = gql`
  query ProcessorRulesList(
    $first: Int
    $after: String
    $last: Int
    $before: String
    $status: [FilterProcessorRuleStatus]
    $actions: [FilterProcessorRuleActions]
    $ruleType: [FilterProcessorRuleRuleType]
    $nameOrGroup: String
    $target: [FilterProcessorRuleTarget]
  ) {
    processorRules(
      first: $first
      after: $after
      last: $last
      before: $before
      status: $status
      actions: $actions
      ruleType: $ruleType
      nameOrGroup: $nameOrGroup
      target: $target
    ) @connection(key: "processorRules", filter: ["target"]) {
      pageInfo {
        ...PaginatorPageInfo
      }
      edges {
        node {
          id
          name
          ruleType
          actions
          triggerFields
          target
          status
          params {
            action
            values {
              key
              value
            }
          }
          group
          createdOn
          createdBy {
            id
            fullname
          }
          updatedOn
          updatedBy {
            id
            fullname
          }
        }
      }
    }
  }
  ${PageInfo}
`;

/**
 * Mutation to enable processor rules
 */
export const ENABLE_PROCESSOR_RULE_MUTATION = gql`
  mutation($ids: [ID]!) {
    enableProcessorRule(input: { ids: $ids }) {
      successIds
      errors {
        field
        messages
      }
    }
  }
`;

/**
 * Mutation to disable processor rules
 */
export const DISABLE_PROCESSOR_RULE_MUTATION = gql`
  mutation($ids: [ID]!) {
    disableProcessorRule(input: { ids: $ids }) {
      successIds
      errors {
        field
        messages
      }
    }
  }
`;

/**
 * Mutation to delete processor rules
 */
export const DELETE_PROCESSOR_RULE_MUTATION = gql`
  mutation($ids: [ID]!) {
    deleteProcessorRule(input: { ids: $ids }) {
      successIds
      errors {
        field
        messages
      }
    }
  }
`;

/**
 * Default filters in ProcessorRuleList
 */
export const defaultFilters = {
  status: undefined,
  actions: undefined,
  ruleType: undefined,
  nameOrGroup: undefined,
};

/**
 * @description
 * Processor rule list container.
 *
 * @class
 * @augments Component
 */
class ProcessorRuleListContainer extends Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);
    const { initialFilters } = props;
    const {
      numberOfRowsSelected,
      direction,
      cursor,
      ...initFilters
    } = initialFilters;
    this.state = {
      numberOfRowsSelected:
        parseInt(numberOfRowsSelected, 0) || DEFAULT_NUMBER_OF_ROWS,
      filters: {
        ...defaultFilters,
        ...initFilters,
      },
      pagination: { direction: direction || null, cursor: cursor || undefined },
      itemProcessing: [],
      failedIds: [],
      showNotification: false,
    };
    this.Compose = adopt({
      mutateProcessorRuleEnable: this.processorRuleEnable,
      mutateProcessorRuleDisable: this.processorRuleDisable,
      mutateProcessorRuleDelete: this.processorRuleDelete,
      query: this.listProcessorRules,
    });
    this.fetchMore = () => {};
  }

  getProcessorRulesQueryVariables = () => {
    const { filters, pagination, numberOfRowsSelected } = this.state;
    const { target } = this.props;
    const paginationVariables =
      !pagination.direction || pagination.direction === 'NEXT'
        ? {
            first: numberOfRowsSelected,
            after: pagination.cursor,
          }
        : {
            last: numberOfRowsSelected,
            before: pagination.cursor,
          };

    return {
      ...filters,
      ...paginationVariables,
      ...getProcessorTargetVariables(target),
    };
  };

  /**
   * @description
   * Query callback to compose with mutations.
   *
   * @param {*} render - Query's render props.
   * @returns {*} Query component.
   */
  listProcessorRules = ({ render }) => (
    <Query
      query={PROCESSOR_RULES_QUERY}
      variables={this.getProcessorRulesQueryVariables()}
      notifyOnNetworkStatusChange
      fetchPolicy="cache-and-network"
    >
      {({ data, loading, fetchMore }) => {
        this.fetchMore = fetchMore;
        return render({ data, loading, fetchMore });
      }}
    </Query>
  );

  /**
   * @description
   * Mutation enable processor rule callback.
   *
   * @param {*} render - Mutation's render props.
   * @returns {*} Mutation component.
   */
  processorRuleEnable = ({ render }) => {
    const { itemProcessing, filters } = this.state;
    return (
      <Mutation
        mutation={ENABLE_PROCESSOR_RULE_MUTATION}
        onCompleted={this.mutationCompleted(
          'enableProcessorRule',
          itemProcessing,
        )}
        update={(cache, { data: { enableProcessorRule } }) => {
          const data = {
            status: processorRulesStatus.ENABLED,
            __typename: 'AdminProcessorRuleType',
          };

          bulkUpdate(
            cache,
            'AdminProcessorRuleType',
            'Enable',
            'status',
            data,
            enableProcessorRule.successIds,
          );

          if (
            filters.status &&
            filters.status.indexOf(processorRulesStatus.ENABLED) === -1
          ) {
            const queryVariables = this.getProcessorRulesQueryVariables();
            const variables = { last: enableProcessorRule.successIds.length };
            const { processorRules } = cache.readQuery({
              query: PROCESSOR_RULES_QUERY,
              variables: queryVariables,
            });

            const newEdges = processorRules.edges.filter(
              edge =>
                enableProcessorRule.successIds.indexOf(edge.node.id) === -1,
            );

            cache.writeQuery({
              query: PROCESSOR_RULES_QUERY,
              variables: queryVariables,
              data: {
                processorRules: {
                  ...processorRules,
                  edges: newEdges,
                },
              },
            });

            this.fetchMore({
              variables,
              updateQuery: this.updateQuery,
            });
          }
        }}
      >
        {handleProcessorRuleEnable => render({ handleProcessorRuleEnable })}
      </Mutation>
    );
  };

  /**
   * @description
   * Mutation disable processor rule callback.
   *
   * @param {*} render - Mutation's render props.
   * @returns {*} Mutation component.
   */
  processorRuleDisable = ({ render }) => {
    const { itemProcessing, filters } = this.state;
    return (
      <Mutation
        mutation={DISABLE_PROCESSOR_RULE_MUTATION}
        onCompleted={this.mutationCompleted(
          'disableProcessorRule',
          itemProcessing,
        )}
        update={(cache, { data: { disableProcessorRule } }) => {
          const data = {
            status: processorRulesStatus.DISABLED,
            __typename: 'AdminProcessorRuleType',
          };

          bulkUpdate(
            cache,
            'AdminProcessorRuleType',
            'Disable',
            'status',
            data,
            disableProcessorRule.successIds,
          );

          if (
            filters.status &&
            filters.status.indexOf(processorRulesStatus.DISABLED) === -1
          ) {
            const queryVariables = this.getProcessorRulesQueryVariables();
            const variables = { last: disableProcessorRule.successIds.length };
            const { processorRules } = cache.readQuery({
              query: PROCESSOR_RULES_QUERY,
              variables: queryVariables,
            });

            const newEdges = processorRules.edges.filter(
              edge =>
                disableProcessorRule.successIds.indexOf(edge.node.id) === -1,
            );

            cache.writeQuery({
              query: PROCESSOR_RULES_QUERY,
              variables: queryVariables,
              data: {
                processorRules: {
                  ...processorRules,
                  edges: newEdges,
                },
              },
            });

            this.fetchMore({
              variables,
              updateQuery: this.updateQuery,
            });
          }
        }}
      >
        {handleProcessorRuleDisable => render({ handleProcessorRuleDisable })}
      </Mutation>
    );
  };

  /**
   * @description
   * Mutation delete processor rule callback.
   *
   * @param {*} render - Mutation's render props.
   * @returns {*} Mutation component.
   */
  processorRuleDelete = ({ render }) => {
    const { itemProcessing, filters } = this.state;
    return (
      <Mutation
        mutation={DELETE_PROCESSOR_RULE_MUTATION}
        onCompleted={this.mutationCompleted(
          'deleteProcessorRule',
          itemProcessing,
        )}
        update={(cache, { data: { deleteProcessorRule } }) => {
          const data = {
            status: processorRulesStatus.DELETED,
            __typename: 'AdminProcessorRuleType',
          };

          bulkUpdate(
            cache,
            'AdminProcessorRuleType',
            'Disable',
            'status',
            data,
            deleteProcessorRule.successIds,
          );

          if (
            filters.status &&
            filters.status.indexOf(processorRulesStatus.DELETED) === -1
          ) {
            const queryVariables = this.getProcessorRulesQueryVariables();
            const variables = { last: deleteProcessorRule.successIds.length };
            const { processorRules } = cache.readQuery({
              query: PROCESSOR_RULES_QUERY,
              variables: queryVariables,
            });

            const newEdges = processorRules.edges.filter(
              edge =>
                deleteProcessorRule.successIds.indexOf(edge.node.id) === -1,
            );

            cache.writeQuery({
              query: PROCESSOR_RULES_QUERY,
              variables: queryVariables,
              data: {
                processorRules: {
                  ...processorRules,
                  edges: newEdges,
                },
              },
            });

            this.fetchMore({
              variables,
              updateQuery: this.updateQuery,
            });
          }
        }}
      >
        {handleProcessorRuleDelete => render({ handleProcessorRuleDelete })}
      </Mutation>
    );
  };

  /**
   * @description
   * Query helpful function to update query's result.
   *
   * @param {Object} previousResult - Latest Query result.
   * @param {Object} options - UpdateQuery options.
   * @returns {Object} New or Previous query's result.
   */
  updateQuery = (previousResult, options) => {
    const {
      pageInfo,
      edges: newEdges,
    } = options.fetchMoreResult.processorRules;
    if (newEdges.length !== 0) {
      return {
        processorRules: {
          __typename: previousResult.processorRules.__typename,
          edges: [...previousResult.processorRules.edges, ...newEdges],
          pageInfo: {
            ...previousResult.processorRules.pageInfo,
            hasNextPage: pageInfo.hasNextPage,
          },
        },
      };
    }
    return previousResult;
  };

  /**
   * @description
   * OnComplete mutations callback.
   *
   * @param {Object} field - Target field of mutation.
   * @param {Object} itemProcessing - Ids of items that is processing.
   * @returns {undefined} - No returns value.
   */
  mutationCompleted = (field, itemProcessing) => async data => {
    const failedIds = itemProcessing.filter(
      item => !data[field].successIds.includes(item),
    );
    this.setState({
      itemProcessing: [],
      failedIds,
      showNotification: failedIds.length > 0,
    });
  };

  /**
   * @description
   * Filter rows according selected filters.
   *
   * @param {Object} filters - Filters to apply.
   */
  handleFilter = filters => {
    const history = this.context;
    this.setState({
      filters: {
        ...defaultFilters,
        ...filters,
      },
      pagination: { direction: null, cursor: undefined },
    });
    const currentLocation = history.location.pathname;
    const { numberOfRowsSelected } = getQueryString(history);
    const stringified = qs.stringify({
      ...filters,
      numberOfRowsSelected,
      direction: undefined,
      cursor: undefined,
    });
    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Change state to restart filters.
   */
  handleRestartFilters = () => {
    const history = this.context;
    this.setState({
      filters: { ...defaultFilters },
      pagination: { direction: null, cursor: undefined },
    });
    const currentLocation = history.location.pathname;
    const { numberOfRowsSelected } = getQueryString(history);
    const stringified = qs.stringify({
      numberOfRowsSelected,
    });

    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Change number of rows selected.
   *
   * @param {number} selectedItem - New number of rows selected.
   */
  handleChangeNumberOfRows = selectedItem => {
    const history = this.context;
    this.setState({
      numberOfRowsSelected: selectedItem,
      pagination: { direction: null, cursor: undefined },
    });
    const currentLocation = history.location.pathname;
    const parsed = getQueryString(history);
    const stringified = qs.stringify({
      ...parsed,
      numberOfRowsSelected: selectedItem,
      direction: undefined,
      cursor: undefined,
    });
    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Fetch more rows when user change page.
   *
   * @param {Object} paginatorVariables - Pagination query's variables.
   */
  handleChangePage = ({ direction, cursor }) => {
    const history = this.context;
    this.setState({ pagination: { direction, cursor } });
    const currentLocation = history.location.pathname;
    const parsed = getQueryString(history);
    const stringified = qs.stringify({
      ...parsed,
      direction,
      cursor,
    });
    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Refresh the list.
   * @returns {Function} - Fetch More function.
   */
  handleRefresh = () => {
    this.fetchMore({
      updateQuery: (_, { fetchMoreResult }) => fetchMoreResult,
    });
  };

  /**
   * @description
   * Close notification.
   * @returns {undefined} - No returns value.
   */
  handleCloseNotification = () => {
    this.setState({ showNotification: false });
  };

  /**
   * @description
   * Render AdsList component.
   */
  render() {
    const { routePaths, titleKey } = this.props;
    const {
      numberOfRowsSelected,
      filters,
      itemProcessing,
      failedIds,
      pagination,
      showNotification,
    } = this.state;
    return (
      <this.Compose>
        {({
          mutateProcessorRuleEnable: { handleProcessorRuleEnable },
          mutateProcessorRuleDisable: { handleProcessorRuleDisable },
          mutateProcessorRuleDelete: { handleProcessorRuleDelete },
          query: { loading, data },
        }) => (
          <ProcessorRuleList
            data={data}
            processorRuleEnable={items => {
              this.setState({
                itemProcessing: [...itemProcessing, ...items],
                failedIds: [],
              });
              handleProcessorRuleEnable({
                variables: {
                  ids: items,
                },
              });
            }}
            processorRuleDisable={items => {
              this.setState({
                itemProcessing: [...itemProcessing, ...items],
                failedIds: [],
              });
              handleProcessorRuleDisable({
                variables: {
                  ids: items,
                },
              });
            }}
            processorRuleDelete={items => {
              this.setState({
                itemProcessing: [...itemProcessing, ...items],
                failedIds: [],
              });
              handleProcessorRuleDelete({
                variables: {
                  ids: items,
                },
              });
            }}
            routePaths={routePaths}
            titleKey={titleKey}
            numberOfRowsSelected={numberOfRowsSelected}
            paginationDirection={pagination.direction}
            changePage={paginationVariables => {
              this.handleChangePage(paginationVariables);
            }}
            changeNumberOfRows={this.handleChangeNumberOfRows}
            appliedFilter={filters}
            filter={this.handleFilter}
            refresh={this.handleRefresh}
            restartFilters={this.handleRestartFilters}
            loading={Object.keys(data).length === 0}
            fullLoading={loading}
            itemProcessing={itemProcessing}
            failedIds={failedIds}
            showNotification={showNotification}
            closeNotification={this.handleCloseNotification}
          />
        )}
      </this.Compose>
    );
  }
}

/**
 * ProcessorRuleListContainer PropTypes
 * @param initialFilters - prop initial Filters to apply
 */

ProcessorRuleListContainer.propTypes = {
  initialFilters: PropTypes.shape({}).isRequired,
  routePaths: PropTypes.shape({
    CREATE: PropTypes.string.isRequired,
    DETAILS: PropTypes.string.isRequired,
  }),
  target: PropTypes.string,
  titleKey: PropTypes.string,
};

ProcessorRuleListContainer.defaultProps = {
  routePaths: undefined,
  target: undefined,
  titleKey: undefined,
};

export default ProcessorRuleListContainer;
